const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, "") ?? "";
const DEFAULT_TIMEOUT_MS = 12_000;
const DEFAULT_RETRY_ATTEMPTS = 2;
const DEFAULT_RETRY_DELAY_MS = 240;

const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);

export interface HttpClientRequestOptions {
  url: string;
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | null;
  signal?: AbortSignal;
  timeoutMs?: number;
  retryAttempts?: number;
  retryDelayMs?: number;
}

export interface HttpClientResponse {
  ok: boolean;
  status: number;
  headers: Headers;
  bodyText: string;
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function isRetryableNetworkError(error: unknown) {
  return error instanceof TypeError;
}

function shouldRetryByStatus(status: number) {
  return RETRYABLE_STATUS_CODES.has(status);
}

function getDefaultRetryAttempts(method: string) {
  return method === "GET" ? DEFAULT_RETRY_ATTEMPTS : 0;
}

function wait(delayMs: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

export function buildApiUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseOrigin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;

  return new URL(`${API_BASE_URL}${normalizedPath}`, baseOrigin).toString();
}

export async function httpClient({
  url,
  method = "GET",
  headers,
  body,
  signal,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  retryAttempts,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
}: HttpClientRequestOptions): Promise<HttpClientResponse> {
  const normalizedMethod = method.toUpperCase();
  const maxRetries = Math.max(
    0,
    retryAttempts ?? getDefaultRetryAttempts(normalizedMethod),
  );

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    const controller = new AbortController();
    let didTimeout = false;
    let detachAbortListener: () => void = () => undefined;

    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        const handleAbort = () => controller.abort();
        signal.addEventListener("abort", handleAbort, { once: true });
        detachAbortListener = () =>
          signal.removeEventListener("abort", handleAbort);
      }
    }

    const timeoutId = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        method: normalizedMethod,
        headers,
        body,
        signal: controller.signal,
      });

      const bodyText = await response.text();

      if (
        shouldRetryByStatus(response.status) &&
        attempt < maxRetries
      ) {
        await wait(retryDelayMs * (attempt + 1));
        continue;
      }

      return {
        ok: response.ok,
        status: response.status,
        headers: response.headers,
        bodyText,
      };
    } catch (requestError) {
      if (isAbortError(requestError) && signal?.aborted) {
        throw requestError;
      }

      if (didTimeout) {
        if (attempt < maxRetries) {
          await wait(retryDelayMs * (attempt + 1));
          continue;
        }

        throw new Error(
          `A requisição excedeu o tempo limite de ${timeoutMs}ms.`,
        );
      }

      if (isAbortError(requestError)) {
        throw requestError;
      }

      if (attempt < maxRetries && isRetryableNetworkError(requestError)) {
        await wait(retryDelayMs * (attempt + 1));
        continue;
      }

      throw requestError;
    } finally {
      clearTimeout(timeoutId);
      detachAbortListener();
    }
  }

  throw new Error("A requisição falhou após múltiplas tentativas.");
}
